import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './NewsPage.css';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [platform, setPlatform] = useState('');
  const [sources, setSources] = useState([]);

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, platform]);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = '/api/news/all';
      const params = new URLSearchParams();
      
      if (platform) {
        params.append('platform', platform);
      }

      // Map filter to appropriate parameters
      if (filter === 'articles') {
        params.append('filter', 'articles');
      } else if (filter === 'giveaways') {
        params.append('filter', 'giveaways');
      } else if (filter !== 'all') {
        // For specific giveaway types (game, loot, beta)
        params.append('filter', 'giveaways');
        params.append('type', filter);
      }

      const url = `${endpoint}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      
      if (response.data.success) {
        setNews(response.data.data || []);
        setSources(response.data.sources || []);
      } else {
        setError(response.data.message || 'Failed to load news');
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err.response?.data?.message || 'Failed to load gaming news. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getNewsTypeColor = (type) => {
    switch (type) {
      case 'giveaway':
        return '#4caf50';
      case 'game-release':
        return '#2196f3';
      case 'news-article':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="news-page">
      <div className="news-header">
        <h1>🎮 Gaming News & Updates</h1>
        <p>Stay updated with the latest gaming news, releases, and free giveaways!</p>
      </div>

      <div className="news-filters">
        <div className="filter-group">
          <label>Type:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
            <option value="all">All News & Giveaways</option>
            <option value="articles">Gaming News Only</option>
            <option value="giveaways">Giveaways Only</option>
            <option value="game">Game Keys</option>
            <option value="loot">In-Game Loot</option>
            <option value="beta">Beta Access</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Platform:</label>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="filter-select">
            <option value="">All Platforms</option>
            <option value="pc">PC</option>
            <option value="steam">Steam</option>
            <option value="epic-games-store">Epic Games Store</option>
            <option value="gog">GOG</option>
            <option value="itchio">Itch.io</option>
            <option value="ps4">PlayStation 4</option>
            <option value="ps5">PlayStation 5</option>
            <option value="xbox-one">Xbox One</option>
            <option value="xbox-series-xs">Xbox Series X|S</option>
            <option value="switch">Nintendo Switch</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="vr">VR</option>
            <option value="battlenet">Battle.net</option>
            <option value="origin">Origin</option>
            <option value="drm-free">DRM-Free</option>
          </select>
        </div>

        <button onClick={fetchNews} className="refresh-btn">
          🔄 Refresh
        </button>
      </div>

      {sources.length > 0 && (
        <div className="news-sources">
          <span>Sources: </span>
          {sources.map((source, index) => (
            <span key={index} className="source-badge">{source}</span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading gaming news...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">⚠️ {error}</p>
          <button onClick={fetchNews} className="retry-btn">Try Again</button>
        </div>
      ) : news.length === 0 ? (
        <div className="no-news">
          <p>No news available at the moment. Try adjusting your filters!</p>
        </div>
      ) : (
        <div className="news-grid">
          {news.map((item, index) => (
            <div key={index} className="news-card">
              {item.image && (
                <div className="news-image">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <span 
                    className="news-type-badge" 
                    style={{ backgroundColor: getNewsTypeColor(item.type) }}
                  >
                    {item.type?.replace('-', ' ').toUpperCase()}
                  </span>
                </div>
              )}
              
              <div className="news-content">
                <div className="news-meta">
                  <span className="news-source">{item.source}</span>
                  <span className="news-date">{formatDate(item.publishedAt)}</span>
                </div>
                
                <h3 className="news-title">{item.title}</h3>
                
                {item.description && (
                  <p className="news-description">
                    {item.description.length > 150 
                      ? item.description.substring(0, 150) + '...' 
                      : item.description}
                  </p>
                )}
                
                {item.platforms && (
                  <div className="news-platforms">
                    <strong>Platforms:</strong> {item.platforms}
                  </div>
                )}
                
                {item.worth && (
                  <div className="news-worth">
                    💰 Worth: {item.worth}
                  </div>
                )}
                
                {item.endDate && item.endDate !== 'N/A' && (
                  <div className="news-end-date">
                    ⏰ Ends: {new Date(item.endDate).toLocaleDateString()}
                  </div>
                )}
                
                <a 
                  href={item.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="news-link"
                >
                  {item.type === 'news-article' ? '📖 Read Article' : '🎁 Claim Giveaway'} →
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsPage;
